import { Glass3DPoint } from './glass-3d-regions';
import { GlassSegmentKey } from './glass-scoring';

export type Glass3DVesselSide = 'L' | 'R' | 'C';
export type Glass3DVesselKind = 'artery' | 'vein';
export type Glass3DVesselGroup =
  | GlassSegmentKey
  | 'HN'
  | 'TX'
  | 'UA'
  | 'AB'
  | 'VEN_AB'
  | 'VEN_LE';

export interface Glass3DVesselDefinition {
  id: string;
  label: string;
  side: Glass3DVesselSide;
  group: Glass3DVesselGroup;
  kind: Glass3DVesselKind;
  segment: GlassSegmentKey | null;
  points: Glass3DPoint[];
}

type RawVesselDefinition = {
  id: string;
  label: string;
  group?: Glass3DVesselGroup;
  kind?: Glass3DVesselKind;
  segment?: GlassSegmentKey | null;
  side?: Glass3DVesselSide;
  pts?: Glass3DPoint[];
  ptsL?: Glass3DPoint[];
};

const RAW_VESSELS: RawVesselDefinition[] = [
  { id: 'aorta', label: 'Infrarenal Aorta', side: 'C', segment: 'AI', pts: [[0, 14.6, -0.05], [0, 13.6, 0], [0.05, 12.4, 0.05], [0.05, 11.2, 0.05], [0, 10.4, 0.05]] },
  { id: 'ci', label: 'Common Iliac', segment: 'AI', ptsL: [[0, 10.4, 0.05], [-0.35, 9.8, 0.1], [-0.65, 9.1, 0.2], [-0.85, 8.5, 0.3]] },
  { id: 'ei', label: 'External Iliac', segment: 'AI', ptsL: [[-0.85, 8.5, 0.3], [-1, 7.9, 0.45], [-1.05, 7.2, 0.55]] },
  { id: 'cfa', label: 'Common / Deep Femoral', segment: 'FP', ptsL: [[-1.05, 7.2, 0.55], [-1.1, 6.7, 0.55], [-1.15, 6.3, 0.45]] },
  { id: 'sfa', label: 'Superficial Femoral (SFA)', segment: 'FP', ptsL: [[-1.15, 6.3, 0.45], [-1.1, 5.5, 0.3], [-1, 4.6, 0.05], [-0.95, 3.9, -0.2]] },
  { id: 'pop', label: 'Popliteal', segment: 'FP', ptsL: [[-0.95, 3.9, -0.2], [-0.95, 3.55, -0.45], [-0.95, 3.2, -0.5]] },
  { id: 'tpt', label: 'Tibioperoneal Trunk', segment: 'IP', ptsL: [[-0.95, 3.2, -0.5], [-0.92, 2.85, -0.45]] },
  { id: 'at', label: 'Anterior Tibial', segment: 'IP', ptsL: [[-0.92, 2.85, -0.45], [-0.85, 2.3, -0.1], [-0.8, 1.6, 0.3], [-0.78, 0.85, 0.5], [-0.78, 0.4, 0.55]] },
  { id: 'pt', label: 'Posterior Tibial', segment: 'IP', ptsL: [[-0.92, 2.85, -0.45], [-0.92, 2.1, -0.4], [-0.88, 1.3, -0.3], [-0.85, 0.55, -0.2]] },
  { id: 'pedal', label: 'Pedal Arch', segment: 'PE', ptsL: [[-0.78, 0.4, 0.55], [-0.65, 0.18, 0.85], [-0.5, 0.1, 1.05], [-0.4, 0.08, 1.25]] },

  { id: 'heart', label: 'Heart', side: 'C', group: 'TX', pts: [[0.3, 15.4, 0.45]] },
  { id: 'aorta_asc', label: 'Ascending Aorta', side: 'C', group: 'TX', pts: [[0.3, 15.4, 0.45], [0.1, 16.05, 0.3], [-0.1, 16.55, 0.15]] },
  { id: 'aorta_arch', label: 'Aortic Arch', side: 'C', group: 'TX', pts: [[-0.1, 16.55, 0.15], [-0.05, 16.85, 0], [0.1, 16.85, -0.1], [0.25, 16.55, -0.15]] },
  { id: 'aorta_desc', label: 'Descending Thoracic Aorta', side: 'C', group: 'TX', pts: [[0.25, 16.55, -0.15], [0.18, 15.6, -0.1], [0.1, 14.6, -0.05]] },
  { id: 'pulm_trunk', label: 'Pulmonary Trunk', side: 'C', group: 'TX', pts: [[0.05, 15.55, 0.35], [0, 16.1, 0.15], [-0.1, 16.3, 0]] },
  { id: 'pulm_a', label: 'Pulmonary Artery', group: 'TX', ptsL: [[-0.1, 16.3, 0], [-0.55, 16.2, 0.05], [-0.95, 15.95, 0.1]] },
  { id: 'coronary', label: 'Coronary Artery', group: 'TX', ptsL: [[0.05, 15.7, 0.55], [-0.2, 15.4, 0.65], [-0.35, 15.1, 0.55], [-0.3, 14.85, 0.45]] },
  { id: 'subclav_a', label: 'Subclavian Artery', group: 'TX', ptsL: [[-0.05, 16.85, -0.05], [-0.7, 17.15, 0.05], [-1.4, 17.1, 0.15]] },
  { id: 'axill_a', label: 'Axillary Artery', group: 'TX', ptsL: [[-1.4, 17.1, 0.15], [-1.95, 16.65, 0.2], [-2.3, 15.95, 0.2]] },

  { id: 'brach_a', label: 'Brachial Artery', group: 'UA', ptsL: [[-2.3, 15.95, 0.2], [-2.55, 14.95, 0.25], [-2.7, 13.95, 0.25], [-2.8, 12.95, 0.2]] },
  { id: 'radial_a', label: 'Radial Artery', group: 'UA', ptsL: [[-2.8, 12.95, 0.2], [-2.95, 12.1, 0.3], [-3.1, 11.2, 0.35], [-3.2, 10.3, 0.4]] },
  { id: 'ulnar_a', label: 'Ulnar Artery', group: 'UA', ptsL: [[-2.8, 12.95, 0.2], [-2.85, 12.1, 0.1], [-2.9, 11.2, 0.05], [-2.95, 10.3, 0.05]] },
  { id: 'digital_a', label: 'Digital Artery', group: 'UA', ptsL: [[-3.1, 10.1, 0.35], [-3.25, 9.7, 0.45], [-3.35, 9.3, 0.5], [-3.4, 8.95, 0.5]] },

  { id: 'common_carotid', label: 'Common Carotid Artery', group: 'HN', ptsL: [[-0.08, 16.85, 0.1], [-0.18, 17.55, 0.15], [-0.25, 18.2, 0.2], [-0.3, 18.7, 0.2]] },
  { id: 'internal_carotid', label: 'Internal Carotid Artery', group: 'HN', ptsL: [[-0.3, 18.7, 0.2], [-0.32, 19.15, 0.15], [-0.32, 19.55, 0.1]] },
  { id: 'external_carotid', label: 'External Carotid Artery', group: 'HN', ptsL: [[-0.3, 18.7, 0.2], [-0.42, 19.1, 0.3], [-0.48, 19.45, 0.35]] },
  { id: 'vertebral_a', label: 'Vertebral Artery', group: 'HN', ptsL: [[-0.2, 17.2, -0.2], [-0.2, 18, -0.25], [-0.18, 18.8, -0.25], [-0.12, 19.45, -0.2]] },
  { id: 'basilar_a', label: 'Basilar Artery', side: 'C', group: 'HN', pts: [[0, 19.55, -0.18], [0, 19.85, -0.15], [0, 20.1, -0.1]] },

  { id: 'celiac', label: 'Celiac Trunk', side: 'C', group: 'AB', pts: [[0.05, 13.6, 0], [0.05, 13.45, 0.1], [0.1, 13.35, 0.2]] },
  { id: 'splenic_a', label: 'Splenic Artery', group: 'AB', ptsL: [[0.1, 13.35, 0.2], [-0.3, 13.3, 0.25], [-0.7, 13.2, 0.3]] },
  { id: 'sma', label: 'Superior Mesenteric Artery', side: 'C', group: 'AB', pts: [[0.05, 13.1, 0], [0.05, 12.85, 0.15], [0.05, 12.55, 0.3], [0.05, 12.2, 0.4]] },
  { id: 'renal_a', label: 'Renal Artery', group: 'AB', ptsL: [[0.05, 12.55, 0], [-0.3, 12.55, -0.05], [-0.65, 12.55, -0.1]] },
  { id: 'gonadal_a', label: 'Gonadal Artery', group: 'AB', ptsL: [[0.05, 12.2, 0.05], [-0.25, 11.4, 0.15], [-0.4, 10.6, 0.25]] },
  { id: 'internal_iliac_a', label: 'Internal Iliac Artery', group: 'AB', ptsL: [[-0.85, 8.5, 0.3], [-0.9, 8.2, 0.15], [-0.85, 7.95, 0]] },

  { id: 'deep_femoral', label: 'Deep Femoral (Profunda)', segment: 'FP', ptsL: [[-1.05, 7.2, 0.55], [-1.2, 6.75, 0.4], [-1.35, 6.3, 0.2], [-1.4, 5.85, 0]] },
  { id: 'peroneal_a', label: 'Peroneal Artery', segment: 'IP', ptsL: [[-0.92, 2.85, -0.45], [-0.95, 2.2, -0.3], [-0.95, 1.4, -0.15], [-0.92, 0.7, -0.05]] },
  { id: 'arcuate_a', label: 'Arcuate Artery', segment: 'PE', ptsL: [[-0.78, 0.3, 0.7], [-0.6, 0.18, 0.95], [-0.45, 0.1, 1.1], [-0.35, 0.08, 1.2]] },
  { id: 'dorsal_digital_a', label: 'Dorsal Digital Artery', segment: 'PE', ptsL: [[-0.4, 0.08, 1.25], [-0.35, 0.06, 1.45], [-0.3, 0.05, 1.6]] },

  { id: 'ivc', label: 'Inferior Vena Cava', side: 'C', group: 'VEN_AB', kind: 'vein', pts: [[0.3, 10.4, 0.1], [0.3, 11.3, 0.1], [0.3, 12.2, 0.1], [0.3, 13, 0.15]] },
  { id: 'renal_v', label: 'Renal Vein', group: 'VEN_AB', kind: 'vein', ptsL: [[0.3, 12.55, 0.1], [-0.2, 12.55, 0.05], [-0.65, 12.55, -0.05]] },
  { id: 'gonadal_v', label: 'Gonadal Vein', group: 'VEN_AB', kind: 'vein', ptsL: [[-0.5, 12.5, -0.05], [-0.45, 11.5, 0.1], [-0.45, 10.6, 0.25]] },
  { id: 'common_iliac_v', label: 'Common Iliac Vein', group: 'VEN_AB', kind: 'vein', ptsL: [[0.3, 10.4, 0.1], [0, 9.85, 0.15], [-0.4, 9.2, 0.25], [-0.75, 8.6, 0.35]] },
  { id: 'external_iliac_v', label: 'External Iliac Vein', group: 'VEN_AB', kind: 'vein', ptsL: [[-0.75, 8.6, 0.35], [-0.9, 7.95, 0.5], [-0.95, 7.3, 0.55]] },
  { id: 'internal_iliac_v', label: 'Internal Iliac Vein', group: 'VEN_AB', kind: 'vein', ptsL: [[-0.75, 8.6, 0.35], [-0.8, 8.2, 0.15], [-0.75, 7.95, 0]] },

  { id: 'common_femoral_v', label: 'Common Femoral Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.95, 7.3, 0.55], [-1, 6.95, 0.55], [-1.05, 6.55, 0.5]] },
  { id: 'deep_femoral_v', label: 'Deep Femoral Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-1.05, 6.55, 0.5], [-1.2, 6.1, 0.35], [-1.3, 5.65, 0.15], [-1.3, 5.2, 0]] },
  { id: 'femoral_v', label: 'Femoral Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-1.05, 6.55, 0.5], [-1, 5.7, 0.35], [-0.9, 4.8, 0.1], [-0.85, 4.05, -0.15]] },
  { id: 'popliteal_v', label: 'Popliteal Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.85, 4.05, -0.15], [-0.85, 3.65, -0.4], [-0.85, 3.25, -0.45]] },
  { id: 'at_v', label: 'Anterior Tibial Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.85, 2.9, -0.4], [-0.78, 2.3, -0.05], [-0.73, 1.6, 0.3], [-0.72, 0.85, 0.5]] },
  { id: 'pt_v', label: 'Posterior Tibial Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.85, 2.9, -0.4], [-0.85, 2.1, -0.35], [-0.82, 1.3, -0.25], [-0.8, 0.55, -0.15]] },
  { id: 'peroneal_v', label: 'Peroneal Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.85, 2.9, -0.4], [-0.9, 2.2, -0.25], [-0.9, 1.4, -0.1], [-0.88, 0.7, -0.02]] },
  { id: 'great_saphenous_v', label: 'Great Saphenous Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.55, 0.55, 0.35], [-0.65, 1.4, 0.4], [-0.75, 2.4, 0.45], [-0.85, 3.4, 0.5], [-0.95, 4.4, 0.55], [-1, 5.5, 0.55], [-1, 6.55, 0.55], [-0.95, 7.3, 0.55]] },
  { id: 'small_saphenous_v', label: 'Small Saphenous Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.65, 0.5, -0.1], [-0.75, 1.2, -0.2], [-0.85, 2.1, -0.3], [-0.85, 3, -0.4]] },
  { id: 'dorsal_venous_arch', label: 'Dorsal Venous Arch', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.65, 0.2, 0.8], [-0.55, 0.18, 1], [-0.45, 0.18, 1.15], [-0.4, 0.2, 1.25]] },
  { id: 'dorsal_digital_v', label: 'Dorsal Digital Vein', group: 'VEN_LE', kind: 'vein', ptsL: [[-0.4, 0.2, 1.25], [-0.35, 0.2, 1.45], [-0.3, 0.2, 1.6]] },
];

export const GLASS_3D_VESSELS: Glass3DVesselDefinition[] = RAW_VESSELS.flatMap((vessel) => {
  const group = vessel.group ?? vessel.segment ?? 'AI';
  const kind = vessel.kind ?? 'artery';
  const segment = vessel.segment ?? (group === 'AI' || group === 'FP' || group === 'IP' || group === 'PE' ? group : null);

  if (vessel.pts) {
    return [{
      id: vessel.id,
      label: vessel.label,
      side: vessel.side ?? 'C',
      group,
      kind,
      segment,
      points: vessel.pts.map((point) => [...point] as Glass3DPoint),
    }];
  }

  if (!vessel.ptsL) {
    return [];
  }

  return (['L', 'R'] as const).map((side) => ({
    id: `${vessel.id}_${side.toLowerCase()}`,
    label: vessel.label,
    side,
    group,
    kind,
    segment,
    points: vessel.ptsL!.map((point) =>
      side === 'L'
        ? ([...point] as Glass3DPoint)
        : ([-point[0], point[1], point[2]] as Glass3DPoint),
    ),
  }));
});
